import { EMPTY_LIST } from "@shared/config/constants";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@shared/ui/card";
import { Skeleton } from "@shared/ui/skeleton";
import { FolderOpen, Plus, Trash2 } from "lucide-react";
import { FormattedMessage } from "react-intl";

type Bucket = {
	id: string;
	name: string;
	public: boolean;
	created_at: string | null;
	updated_at: string | null;
};

type Props = {
	buckets: Bucket[];
	isPending: boolean;
	activeBucket: string;
	onSelect: (name: string) => void;
	onDelete: (name: string) => void;
	onAdd: () => void;
};

export function BucketsList({
	buckets,
	isPending,
	activeBucket,
	onSelect,
	onDelete,
	onAdd,
}: Props) {
	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between">
				<div>
					<CardTitle className="flex items-center gap-2 text-base">
						<FolderOpen className="h-4 w-4 text-muted-foreground" />
						<FormattedMessage id="page.storage.buckets.title" defaultMessage="Buckets" />
					</CardTitle>
					<CardDescription>
						<FormattedMessage
							id="page.storage.buckets.description"
							defaultMessage="Select to browse"
						/>
					</CardDescription>
				</div>
				<Button variant="ghost" size="icon" className="h-8 w-8" onClick={onAdd}>
					<Plus className="h-4 w-4" />
				</Button>
			</CardHeader>
			<CardContent className="p-2">
				{isPending ? (
					<div className="space-y-2 p-2">
						{EMPTY_LIST.map((listItem) => (
							<Skeleton key={listItem} className="h-12 w-full rounded-md" />
						))}
					</div>
				) : (
					<ul className="space-y-1">
						{buckets.map((b) => (
							<li key={b.name} className="group relative">
								<button
									type="button"
									onClick={() => onSelect(b.name)}
									className={`flex w-full items-start justify-between rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-accent ${
										activeBucket === b.name ? "bg-accent" : ""
									}`}
								>
									<div className="min-w-0">
										<div className="flex items-center gap-2">
											<span className="font-medium">{b.name}</span>
											<Badge
												variant={b.public ? "secondary" : "outline"}
												className="h-4 px-1.5 text-[10px]"
											>
												{b.public ? (
													<FormattedMessage
														id="page.storage.buckets.public"
														defaultMessage="public"
													/>
												) : (
													<FormattedMessage
														id="page.storage.buckets.private"
														defaultMessage="private"
													/>
												)}
											</Badge>
										</div>
										<p className="mt-0.5 text-xs text-muted-foreground">
											{b.updated_at ? new Date(b.updated_at).toLocaleDateString() : "—"}
										</p>
									</div>
								</button>
								<Button
									variant="ghost"
									size="icon"
									className="absolute right-1 top-1/2 hidden h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-destructive group-hover:flex"
									onClick={() => onDelete(b.name)}
								>
									<Trash2 className="h-3.5 w-3.5" />
								</Button>
							</li>
						))}
					</ul>
				)}
			</CardContent>
		</Card>
	);
}
